import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import { financeReportService } from '../../services/financeReport.service';
import { Calendar, ChevronDown, ChevronUp } from 'lucide-react';

export const YearlyFinanceReportTab: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [expandedMonth, setExpandedMonth] = useState<number | null>(null);

  const { data: report, isLoading, error } = useQuery({
    queryKey: ['finance-yearly-report', year],
    queryFn: () => financeReportService.getYearlyReport(year),
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const toggleMonth = (month: number) => {
    setExpandedMonth(expandedMonth === month ? null : month);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Year"
          type="number"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          min={2020}
          max={2050}
        />
        <div className="flex items-end">
          <Button onClick={() => {}} className="w-full">
            <Calendar size={16} className="mr-2" />
            View Yearly Report
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="text-center py-8 text-gray-500">Loading yearly report...</div>
      )}

      {error && (
        <div className="text-center py-8 text-error">Failed to load yearly report</div>
      )}

      {report && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-primary-green to-green-600 p-6 rounded-lg text-white">
            <h2 className="text-3xl font-bold mb-2">{report.year} Annual Report</h2>
            <p className="opacity-90">Complete financial overview for the year</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Total Trips</p>
              <p className="text-3xl font-bold text-blue-700">{report.totalTrips}</p>
              <p className="text-xs text-gray-500 mt-1">{report.completedTrips} completed</p>
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Total Invoiced</p>
              <p className="text-2xl font-bold text-green-700">{formatCurrency(report.totalInvoiced)}</p>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Total Paid</p>
              <p className="text-2xl font-bold text-yellow-700">{formatCurrency(report.totalPaid)}</p>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Outstanding</p>
              <p className="text-2xl font-bold text-purple-700">{formatCurrency(report.totalOutstanding)}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Total Allowances</p>
              <p className="text-xl font-bold">{formatCurrency(report.totalAllowances)}</p>
              <div className="mt-2 text-xs space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-500">Approved:</span>
                  <span className="font-medium">{formatCurrency(report.approvedAllowances)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Pending:</span>
                  <span className="font-medium">{formatCurrency(report.pendingAllowances)}</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
              <p className="text-xl font-bold text-green-600">{formatCurrency(report.totalRevenue)}</p>
              <p className="text-xs text-gray-500 mt-1">
                Collection rate: {report.totalInvoiced > 0
                  ? `${((report.totalPaid / report.totalInvoiced) * 100).toFixed(1)}%`
                  : '0%'}
              </p>
            </div>

            <div className="bg-gradient-to-r from-primary-green to-green-600 p-4 rounded-lg text-white">
              <p className="text-sm opacity-90 mb-1">Net Profit</p>
              <p className="text-2xl font-bold">{formatCurrency(report.netProfit)}</p>
              <p className="text-xs opacity-75 mt-1">
                {report.totalRevenue > 0
                  ? `${((report.netProfit / report.totalRevenue) * 100).toFixed(1)}% margin`
                  : '0% margin'}
              </p>
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Monthly Breakdown</h3>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead className="text-right">Trips</TableHead>
                    <TableHead className="text-right">Invoiced</TableHead>
                    <TableHead className="text-right">Paid</TableHead>
                    <TableHead className="text-right">Outstanding</TableHead>
                    <TableHead className="text-right">Allowances</TableHead>
                    <TableHead className="text-right">Net Profit</TableHead>
                    <TableHead> </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.monthlyReports.map((monthReport) => (
                    <React.Fragment key={monthReport.month}>
                      <TableRow className="hover:bg-gray-50">
                        <TableCell className="font-medium">{monthReport.monthName}</TableCell>
                        <TableCell className="text-right">{monthReport.totalTrips}</TableCell>
                        <TableCell className="text-right">{formatCurrency(monthReport.totalInvoiced)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(monthReport.totalPaid)}</TableCell>
                        <TableCell className="text-right">
                          <span className={monthReport.totalOutstanding > 0 ? 'text-yellow-600' : ''}>
                            {formatCurrency(monthReport.totalOutstanding)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(monthReport.approvedAllowances)}</TableCell>
                        <TableCell className="text-right">
                          <span className={monthReport.netProfit > 0 ? 'text-green-600 font-semibold' : 'text-red-600'}>
                            {formatCurrency(monthReport.netProfit)}
                          </span>
                        </TableCell>
                        <TableCell>
                          {monthReport.trips.length > 0 && (
                            <button
                              onClick={() => toggleMonth(monthReport.month)}
                              className="text-primary-green hover:text-green-700"
                            >
                              {expandedMonth === monthReport.month ? (
                                <ChevronUp size={20} />
                              ) : (
                                <ChevronDown size={20} />
                              )}
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                      {expandedMonth === monthReport.month && monthReport.trips.length > 0 && (
                        <tr>
                          <td colSpan={8} className="bg-gray-50 p-4">
                            <div className="text-sm space-y-2">
                              <p className="font-semibold text-gray-700 mb-2">
                                {monthReport.trips.length} trips in {monthReport.monthName}
                              </p>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {monthReport.trips.map((trip) => (
                                  <div key={trip.tripId} className="bg-white p-2 rounded border">
                                    <div className="font-medium text-xs">{trip.tripNumber}</div>
                                    <div className="text-xs text-gray-600">{trip.driverName}</div>
                                    <div className="text-xs text-gray-500">
                                      {trip.origin} → {trip.destination}
                                    </div>
                                    <div className="text-xs font-semibold text-green-600 mt-1">
                                      Profit: {formatCurrency(trip.netProfit)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm text-gray-600">Avg Monthly Revenue</p>
              <p className="text-xl font-bold">
                {formatCurrency(report.totalRevenue / 12)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Avg Monthly Profit</p>
              <p className="text-xl font-bold text-green-600">
                {formatCurrency(report.netProfit / 12)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Avg Trips/Month</p>
              <p className="text-xl font-bold">
                {(report.totalTrips / 12).toFixed(1)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Profit per Trip</p>
              <p className="text-xl font-bold text-primary-green">
                {report.totalTrips > 0
                  ? formatCurrency(report.netProfit / report.totalTrips)
                  : formatCurrency(0)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
